import { Box, Button, DialogActions, DialogContent, DialogTitle, ModalClose, ModalDialog } from '@mui/joy'
import { useApi } from 'api'
import Modal, { dontCloseOnBackgroundClick } from 'components/modal'
import { useListingContext } from 'listings/listingProvider'
import { prop } from 'ramda'
import { useEffect, useRef, useState } from 'react'

interface IframeModalProps {
    title: string
    url: string | undefined
    onClose: () => void
    signingService?: 'docusign' | 'zoho' | 'annature'
    listingId?: string
    formType?: string
    documentId?: string
}

const IframeModal = ({
    title,
    url,
    onClose: onCloseProp,
    signingService = 'annature',
    listingId,
    formType,
    documentId,
}: IframeModalProps) => {
    const api = useApi()
    const listingContext = useListingContext()
    const setListing = listingContext?.setListing
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [saveError, setSaveError] = useState<string>()
    const [sendForm, setSendForm] = useState(false)

    const onClose = () => {
        onCloseProp()
        setSendForm(false)
        setSaveError(undefined)
        setIsSaving(false)
    }

    const sendFormViaApi = async () => {
        try {
            const { updatedListing } = await api
                .post(`listing/sale/${listingId}/${signingService}/${formType}/send`, {
                    formType,
                    formId: documentId,
                })
                .then(prop('data'))
            setListing?.(updatedListing)
            setIsSaving(false)
            onClose()
        } catch (error: any) {
            console.error('Error sending form:', error)
            setSaveError(error.response?.data?.message || 'Error sending form')
        }
    }

    useEffect(() => {
        const handleLoad = (event: MessageEvent) => {
            // Handle Annature save responses
            if (event.data.event === 'annatureSaveSuccess') {
                setSaveError(undefined)

                // If we have the necessary props, send the form via API
                console.log('sendForm', sendForm)
                if (sendForm) {
                    sendFormViaApi()
                } else {
                    onClose()
                }
            }
            if (event.data.event === 'annatureSaveError') {
                console.error('Annature save failed:', event.data.message)
                setIsSaving(false)
                setSaveError(event.data.message || 'Save failed')
            }
        }
        window.addEventListener('message', handleLoad)
        return () => {
            window.removeEventListener('message', handleLoad)
        }
    }, [onClose, listingId, formType, signingService, sendForm])

    const handleSaveAndClose = () => {
        if (signingService === 'annature' && iframeRef.current) {
            setIsSaving(true)
            setSaveError(undefined)
            setSendForm(false)
            // Send save message to Annature iframe
            iframeRef.current.contentWindow?.postMessage('annatureSave', '*')
        }
    }

    const handleSaveAndSend = () => {
        if (signingService === 'annature' && iframeRef.current) {
            setIsSaving(true)
            setSaveError(undefined)
            setSendForm(true)
            // Send save message to Annature iframe
            iframeRef.current.contentWindow?.postMessage('annatureSave', '*')
            // Note: setIsSaving(false) is handled in the message event handler (see useEffect),
            // so loading will only finish after the iframe responds with 'annatureSaveSuccess' or 'annatureSaveError'.
        }
    }

    const handleCancel = () => {
        onClose()
    }

    const isAnnature = signingService === 'annature'

    return (
        <Modal
            open={url !== undefined}
            onClose={dontCloseOnBackgroundClick(onClose)}
        >
            <ModalDialog sx={{ width: '100%', height: '100%' }}>
                <ModalClose />
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <iframe
                        ref={iframeRef}
                        src={url}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                        }}
                    />
                </DialogContent>
                {saveError && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            color: 'danger.500',
                            bgcolor: 'danger.50',
                            borderTop: 1,
                            borderColor: 'danger.200',
                        }}
                    >
                        Error: {saveError}
                    </Box>
                )}
                <DialogActions>
                    {isAnnature ? (
                        <>
                            <Button
                                onClick={handleSaveAndSend}
                                loading={isSaving}
                                disabled={isSaving}
                            >
                                Save & Send
                            </Button>
                            <Button
                                onClick={handleSaveAndClose}
                                loading={isSaving}
                                disabled={isSaving}
                            >
                                Save & Close
                            </Button>
                            <Box sx={{ flexGrow: 1 }} />
                            <Button
                                variant="outlined"
                                onClick={handleCancel}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button onClick={onClose}>Close</Button>
                    )}
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}

export default IframeModal
